import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, PlatformMark } from '../../ui';
import { relativeTime } from '../../../utils/dates';
import PATHS from '../../../constants/Paths';

export const STALE_AFTER_DAYS = 30;

/**
 * Split accounts into recently scanned and not.
 *
 * Exported and pure so the verdict can count the stragglers without this
 * component having rendered, and so the threshold has one definition.
 */
export function splitCoverage(accounts = [], now = Date.now()) {
	const cutoff = now - STALE_AFTER_DAYS * 86400000;
	const fresh = [];
	const stale = [];
	for (const a of accounts) {
		const t = a.lastScan ? new Date(a.lastScan).getTime() : NaN;
		// Never scanned counts as stale, not as missing data: an account nobody
		// has ever looked at is the most uncovered case there is.
		if (Number.isNaN(t) || t < cutoff) stale.push(a); else fresh.push(a);
	}
	return { fresh, stale };
}

/**
 * Which accounts are actually being watched.
 *
 * The largest gap on the old dashboard: a monitored account nobody scans is an
 * unmonitored account, and nothing said so. Three of nine accounts had gone
 * over a month, invisibly.
 *
 * Not governed by the page's timeframe - "has this been looked at recently" is
 * a question about now - so the heading says its own window rather than
 * inheriting one silently.
 */
export function Coverage({ accounts = [] }) {
	const navigate = useNavigate();
	const { fresh, stale } = splitCoverage(accounts);
	const total = accounts.length;

	if (!total) return null;

	return (
		<Card>
			<CardContent>
				<div className="flex flex-wrap items-baseline justify-between gap-2">
					<h2 className="text-base font-semibold text-foreground">Coverage</h2>
					<span className="text-sm text-muted-foreground">
						{fresh.length} of {total} scanned in the last {STALE_AFTER_DAYS} days
					</span>
				</div>

				<div className="mt-3 flex h-2 overflow-hidden rounded-full bg-muted">
					{fresh.length > 0 && (
						<span className="bg-green-600" style={{ width: `${(fresh.length / total) * 100}%` }} />
					)}
					{stale.length > 0 && (
						<span className="bg-amber-500" style={{ width: `${(stale.length / total) * 100}%` }} />
					)}
				</div>

				{stale.length === 0 ? (
					<p className="mt-3 text-sm text-muted-foreground">Every account has been scanned recently.</p>
				) : (
					<>
						<p className="mt-3 text-sm text-muted-foreground">
							{stale.length} account{stale.length === 1 ? '' : 's'}{' '}
							{stale.length === 1 ? 'has' : 'have'} not been scanned in {STALE_AFTER_DAYS} days.
							An account nobody scans is not being monitored.
						</p>
						<ul className="mt-3 divide-y divide-border rounded-lg border border-border">
							{stale.map((a) => (
								<li key={a.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
									<div className="relative shrink-0">
										<img src={a.avatar || 'https://placehold.co/28x28'} alt="" className="h-7 w-7 rounded-full bg-muted object-cover" />
										{a.platform && <PlatformMark platform={a.platform} size="xs" className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white" />}
									</div>
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-medium text-foreground">@{a.handle}</div>
										{a.clientName && <div className="truncate text-xs text-muted-foreground">{a.clientName}</div>}
									</div>
									<span className="text-xs text-amber-700">
										{a.lastScan ? `last scanned ${relativeTime(a.lastScan)}` : 'never scanned'}
									</span>
									<Button
										size="sm"
										variant="outline"
										onClick={() => navigate(PATHS.NEW_SCANS, { state: { accountId: a.id } })}
									>
										Scan now
									</Button>
								</li>
							))}
						</ul>
					</>
				)}
			</CardContent>
		</Card>
	);
}
